import React from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, AlertCircle, Check } from 'lucide-react';

interface PasswordInputProps {
  password: string;
  isProtected: boolean;
  isCheckingPassword: boolean;
  isPasswordExposed: boolean;
  passwordStrength: { score: number; feedback: string } | null;
  onPasswordChange: (password: string) => void;
}

export default function PasswordInput({
  password,
  isProtected,
  isCheckingPassword,
  isPasswordExposed,
  passwordStrength,
  onPasswordChange,
}: PasswordInputProps) {
  const { t } = useTranslation('createNote');

  const getPasswordColor = () => {
    if (!password) return 'border-gray-200';
    if (isCheckingPassword) return 'border-blue-200 focus:border-blue-300';
    if (isPasswordExposed) return 'border-red-200 focus:border-red-300';
    if (password.length < 6) return 'border-orange-200 focus:border-orange-300';
    if (passwordStrength?.score === 5) return 'border-emerald-200 focus:border-emerald-300';
    if (passwordStrength?.score >= 3) return 'border-green-200 focus:border-green-300';
    return 'border-yellow-200 focus:border-yellow-300';
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        <Lock className="w-4 h-4 inline mr-2" />
        {t('form.password.label')}
      </label>
      <div className="relative">
        <input
          type="password"
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          placeholder={t('form.password.placeholder')}
          className={`
            w-full pl-3 pr-10 py-3 border rounded-xl
            transition-all duration-200
            ${getPasswordColor()}
            focus:outline-none focus:ring-2
            ${password.length === 0 ? 'focus:ring-blue-100' : ''}
            ${isPasswordExposed ? 'focus:ring-red-100' : ''}
            ${password.length > 0 && password.length < 6 ? 'focus:ring-orange-100' : ''}
            ${passwordStrength?.score === 5 ? 'focus:ring-emerald-100' : ''}
            ${passwordStrength?.score >= 3 ? 'focus:ring-green-100' : ''}
            ${passwordStrength?.score < 3 ? 'focus:ring-yellow-100' : ''}
          `}
          minLength={6}
        />
        {password.length > 0 && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            <div 
              className={`
                rounded-full p-1 transition-colors duration-200
                ${isCheckingPassword ? 'text-blue-500 bg-blue-50' : ''}
                ${isPasswordExposed ? 'text-red-500 bg-red-50' : ''}
                ${!isCheckingPassword && !isPasswordExposed && password.length < 6 ? 'text-orange-500 bg-orange-50' : ''}
                ${!isCheckingPassword && !isPasswordExposed && password.length >= 6 ? 'text-emerald-500 bg-emerald-50' : ''}
              `}
            >
              {isCheckingPassword ? (
                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              ) : isPasswordExposed ? (
                <AlertCircle className="w-4 h-4" />
              ) : (
                <Check className="w-4 h-4" />
              )}
            </div>
          </div>
        )}
      </div>
      {password.length > 0 && (
        <div className={`text-xs font-medium mt-2 ml-1 ${
          isCheckingPassword ? 'text-blue-600' :
          isPasswordExposed ? 'text-red-600' :
          password.length < 6 ? 'text-orange-600' :
          'text-gray-600'
        }`}>
          {isCheckingPassword ? (
            t('form.password.validation.checking')
          ) : isPasswordExposed ? (
            t('form.password.validation.exposed')
          ) : password.length < 6 ? (
            t('form.password.validation.moreChars', { count: 6 - password.length })
          ) : (
            passwordStrength?.feedback
          )}
        </div>
      )}
    </div>
  );
}